import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth';
import { Layout } from './components/Layout';
import { Carregando } from './components/ui';
import { CadastroPage, EmpresasPage, EsqueciSenhaPage, LoginPage, RedefinirSenhaPage } from './pages/Auth';
import { CampanhaDetalhePage } from './pages/CampanhaDetalhe';
import { CampanhaFormPage } from './pages/CampanhaForm';
import { CampanhasPage } from './pages/Campanhas';
import { ClienteFichaPage } from './pages/ClienteFicha';
import { ClienteNovoPage } from './pages/ClienteNovo';
import { ClientesPage } from './pages/Clientes';
import { ConfiguracoesPage } from './pages/Configuracoes';
import { ContaPage } from './pages/Conta';
import { PrimeirosPassosPage } from './pages/PrimeirosPassos';
import { DashboardPage } from './pages/Dashboard';
import { DashboardEquipePage } from './pages/DashboardEquipe';
import { FilaPage } from './pages/Fila';
import { LotePage } from './pages/Lote';
import { NegociacoesPage } from './pages/Negociacoes';
import { ProspeccaoPage } from './pages/Prospeccao';
import { RetornosPage } from './pages/Retornos';
import { TemplatesPage } from './pages/Templates';
import { UsuariosPage } from './pages/Usuarios';

// Painel Super Admin: carregado só quando alguém abre /admin (fora do bundle do CRM)
const AdminApp = lazy(() => import('./admin/AdminApp'));

function Protegido({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const loc = useLocation();
  if (status === 'carregando') return <Carregando />;
  if (status === 'anonimo') return <Navigate to="/entrar" replace state={{ de: loc.pathname }} />;
  return <>{children}</>;
}

function SoAnonimo({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === 'carregando') return <Carregando />;
  if (status === 'autenticado') return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route path="/admin/*" element={<Suspense fallback={<Carregando />}><AdminApp /></Suspense>} />
      <Route path="/entrar" element={<SoAnonimo><LoginPage /></SoAnonimo>} />
      <Route path="/cadastro" element={<SoAnonimo><CadastroPage /></SoAnonimo>} />
      <Route path="/empresas" element={<SoAnonimo><EmpresasPage /></SoAnonimo>} />
      <Route path="/esqueci-senha" element={<SoAnonimo><EsqueciSenhaPage /></SoAnonimo>} />
      <Route path="/redefinir-senha" element={<RedefinirSenhaPage />} />
      <Route path="/campanhas/:id/fila" element={<Protegido><FilaPage /></Protegido>} />
      <Route element={<Protegido><Layout /></Protegido>}>
        <Route index element={<DashboardPage />} />
        <Route path="equipe" element={<DashboardEquipePage />} />
        <Route path="clientes" element={<ClientesPage />} />
        <Route path="clientes/novo" element={<ClienteNovoPage />} />
        <Route path="clientes/:id" element={<ClienteFichaPage />} />
        <Route path="prospeccao" element={<ProspeccaoPage />} />
        <Route path="negociacoes" element={<NegociacoesPage />} />
        <Route path="campanhas" element={<CampanhasPage />} />
        <Route path="campanhas/nova" element={<CampanhaFormPage />} />
        <Route path="campanhas/:id" element={<CampanhaDetalhePage />} />
        <Route path="campanhas/:id/editar" element={<CampanhaFormPage />} />
        <Route path="campanhas/:id/lote" element={<LotePage />} />
        <Route path="retornos" element={<RetornosPage />} />
        <Route path="usuarios" element={<UsuariosPage />} />
        <Route path="templates" element={<TemplatesPage />} />
        <Route path="configuracoes" element={<Navigate to="/configuracoes/email" replace />} />
        <Route path="configuracoes/:secao" element={<ConfiguracoesPage />} />
        <Route path="conta" element={<ContaPage />} />
        <Route path="primeiros-passos" element={<PrimeirosPassosPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
